import { Controller } from "stimulus";

export default class DrawFlowController extends Controller {

  get nodeGap() { return 130; }

  initialize() {
    super.initialize();
    document.addEventListener("click", this.hideActionMenu);
    document.addEventListener("flow:visible", () => {
      requestAnimationFrame(() => this.fitToScreen());
    });
    this._interceptNavigation = this._interceptNavigation.bind(this);
    document.addEventListener("click", this._interceptNavigation, true);
  }

  connect() {
    this.setupDrawFlow();
    window.drawFlowController = this;
  };

  disconnect() {
    document.removeEventListener("click", this._interceptNavigation, true);
    if (window.drawFlowController === this) window.drawFlowController = null;
  }

  setupDrawFlow() {
    let container = document.getElementById("flow-canvas");
    const editor = new Drawflow(container);
    this.editor = editor;
    editor.curvature = 0.5;
    editor.line_path = 15;
    editor.reroute = true;
    editor.force_curve = true;
    editor.line_path = 2; // 2 = top-bottom bezier

    editor.start();
    editor.createCurvature = this.editorCreateCurvature;
    let sequence = document.querySelector('#sequence').dataset.sequence
    if (sequence) {
      editor.import(JSON.parse(sequence));
      requestAnimationFrame(() => this.fitToScreen());
    } else {
      const posX = editor.precanvas.clientWidth / 2;
      const posY = editor.precanvas.getBoundingClientRect().y;
      const triggerId = this.addNodeToDrawFlow("trigger", posX, posY);
      const emailId = this.addNodeToDrawFlow("email", posX, posY + (this.nodeGap));
      const filterId = this.addNodeToDrawFlow("filter-continuously-sent", posX, posY + 2 * this.nodeGap);
      const endId = this.addNodeToDrawFlow("end", posX + 105, posY + 4 * this.nodeGap);

      editor.addConnection(triggerId, emailId, "output_1", "input_1");
      editor.addConnection(emailId, filterId, "output_1", "input_1");
      editor.addConnection(filterId, endId, "output_1", "input_1");

      this.refreshConnections();
    }

    this.editorSetupEvents();
    this.setupDragEvent();
    this.setupConnectionClickHandler();
  }

  editorCreateCurvature(xstart, ystart, xend, yend, curvature, type) {
    const dist = Math.abs(yend - ystart);
    const isReverse = ystart >= yend;

    // Default multipliers for the curvature
    let m1 = 1;
    let m2 = 1;

    // Only the 'reverse' direction (bottom-to-top) needs special multiplier logic
    if (isReverse) {
      switch (type) {
        case 'open':  m1 = 1;  m2 = -1; break;
        case 'close': m1 = -1; m2 = 1;  break;
        case 'other': m1 = -1; m2 = -1; break;
      }
    }

    const hy1 = ystart + dist * (curvature * m1);
    const hy2 = yend - dist * (curvature * m2);

    return `M ${xstart} ${ystart} C ${xstart} ${hy1} ${xend} ${hy2} ${xend} ${yend}`;
  }

  editorSetupEvents() {
    const controller = this;
    const editor = this.editor;

    // Events!
    editor.on('nodeCreated', function(id) {
      console.log("Node created " + id);
    })

    editor.on('nodeRemoved', function(id) {
      console.log("Node removed " + id);
    })

    editor.on('nodeSelected', function(id) {
      console.log("Node selected " + id);
    })

    editor.on('moduleCreated', function(name) {
      console.log("Module Created " + name);
    })

    editor.on('moduleChanged', function(name) {
      console.log("Module Changed " + name);
    })

    editor.on('connectionCreated', function(connection) {
      const nodes = editor.drawflow.drawflow.Home.data;

      const outputConns = nodes[connection.output_id]?.outputs[connection.output_class]?.connections || [];
      const inputConns = nodes[connection.input_id]?.inputs[connection.input_class]?.connections || [];

      if (outputConns.length > 1 || inputConns.length > 1) {
        editor.removeSingleConnection(connection.output_id, connection.input_id, connection.output_class, connection.input_class);
        return;
      }

      controller.updateSequenceJson();
    })

    editor.on('connectionRemoved', function(connection) {
      console.log('Connection removed');
      console.log(connection);
      controller.updateSequenceJson();
    })

    editor.on('mouseMove', function(position) {
      controller.updateSequenceJson();
      // console.log('Position mouse x:' + position.x + ' y:'+ position.y);
    })

    editor.on('nodeMoved', function(id) {
      console.log("Node moved " + id);
    })

    editor.on('zoom', function(zoom) {
      document.querySelector(".zoom-level").innerHTML = Math.round(zoom * 100) + '%';
    })

    editor.on('translate', function(position) {
      console.log('Translate x:' + position.x + ' y:'+ position.y);
    })

    editor.on('addReroute', function(id) {
      console.log("Reroute added " + id);
    })

    editor.on('removeReroute', function(id) {
      console.log("Reroute removed " + id);
    })
  }

  updateSequenceJson() {
    this.syncNodeHtmlFromDom();
    document.querySelector('#sequence').dataset.sequence = JSON.stringify(this.editor.export());
  }

  // Drawflow stores each node as a static HTML string. Live changes to
  // <select>/<input> values only live on the DOM element's `.value` property and
  // never make it into that stored string, so export()/import() would otherwise
  // lose every user selection. Reflect the current values back onto the markup and
  // refresh each node's stored html before exporting.
  syncNodeHtmlFromDom() {
    const nodes = this.editor?.drawflow?.drawflow?.Home?.data;
    if (!nodes) return;

    Object.keys(nodes).forEach((id) => {
      const contentEl = document.querySelector(`#node-${id} .drawflow_content_node`);
      if (!contentEl) return;

      this.reflectFormValuesToAttributes(contentEl);
      nodes[id].html = contentEl.innerHTML;
    });
  }

  reflectFormValuesToAttributes(root) {
    root.querySelectorAll("select").forEach((select) => {
      Array.from(select.options).forEach((option) => {
        if (option.value === select.value) {
          option.setAttribute("selected", "selected");
        } else {
          option.removeAttribute("selected");
        }
      });
    });

    root.querySelectorAll("input").forEach((input) => {
      if (input.type === "checkbox" || input.type === "radio") {
        if (input.checked) {
          input.setAttribute("checked", "checked");
        } else {
          input.removeAttribute("checked");
        }
      } else {
        input.setAttribute("value", input.value);
      }
    });

    root.querySelectorAll("textarea").forEach((textarea) => {
      textarea.textContent = textarea.value;
    });
  }

  _interceptNavigation(e) {
    const trigger = e.target.closest('.set-trigger');
    if (!trigger) return;

    const flowSection = document.getElementById('flow-section');
    if (!flowSection || flowSection.classList.contains('section-hidden')) return;

    // Take over navigation entirely: a full page reload would otherwise abort the
    // in-flight persist (which carries the large sequence JSON) before it saves.
    e.preventDefault();
    e.stopImmediatePropagation();

    if (!this.isFlowComplete()) {
      toastr.error("All nodes must be connected to the End node.");
      return;
    }

    const link = trigger.closest('a[href]');
    const href = link && link.getAttribute('href');
    const navigate = () => {
      if (href && href !== '#') window.location.href = href;
    };

    const request = this.syncFlowToTrigger();
    if (request && request.always) {
      request.always(navigate);
    } else {
      navigate();
    }
  }

  isFlowComplete() {
    const nodes = this.editor.drawflow.drawflow.Home.data;
    let currentNode = Object.values(nodes).find(n => n.name === "trigger");
    if (!currentNode) return false;

    while (currentNode) {
      if (currentNode.name === "end") return true;
      const connections = currentNode.outputs?.output_1?.connections;
      currentNode = connections?.length > 0 ? nodes[connections[0].node] : null;
    }
    return false;
  }

  getFlowFilters() {
    const nodes = this.editor.drawflow.drawflow.Home.data;
    let currentNode = Object.values(nodes).find(n => n.name === "trigger");
    if (!currentNode) return null;

    const paramsArray = [{"query_type": "is unknown", "filter_type": "archived_at", "search_field": "archived_at", "search_value": "is unknown"}];
    const skipNodes = ["trigger", "email", "end"];

    while (currentNode) {
      if (currentNode.name === "end") return paramsArray;

      if (!skipNodes.includes(currentNode.name)) {
        const domNode = document.getElementById(`node-${currentNode.id}`);
        if (domNode) {
          const filter = this.extractFilterData(domNode);
          if (filter) paramsArray.push(filter);
        }
      }

      const connections = currentNode.outputs?.output_1?.connections;
      currentNode = connections?.length > 0 ? nodes[connections[0].node] : null;
    }
    return null;
  }

  syncFlowToTrigger() {
    this.updateSequenceJson();
    const filters = this.getFlowFilters();
    if (!filters) return;
    return this.persistTrigger(filters);
  }

  persistTrigger(paramsArray) {
    const sequence = document.querySelector('#sequence').dataset.sequence;
    const interactionId = document.querySelector('#interaction_id')?.dataset?.params;
    if (!interactionId) return;

    const accountId = document.body.dataset.accountId || $("body").data("account-id");
    if (!accountId) return;

    return $.ajax({
      type: "POST",
      url: `/app/${accountId}/interaction/update_trigger`,
      data: { id: interactionId, filters: paramsArray, sequence: sequence },
    });
  }

  switchToTriggersView() {
    const toggle = document.getElementById("trigger-switch");
    if (toggle) toggle.checked = false;

    const flowSection = document.getElementById('flow-section');
    const guestsSection = document.getElementById('guests-section');
    if (flowSection) flowSection.classList.add("section-hidden");
    if (guestsSection) guestsSection.classList.remove("section-hidden");
    document.body.style.overflow = "";
  }

  editorZoomIn() {
    this.editor.zoom_in()
  }

  editorZoomOut() {
    this.editor.zoom_out()
  }

  fitToScreen() {
    const editor = this.editor;
    const nodes = editor.drawflow.drawflow.Home.data;
    const nodeIds = Object.keys(nodes);
    if (nodeIds.length === 0) return;

    const container = document.getElementById("flow-canvas");
    const canvasW = container.clientWidth;
    const canvasH = container.clientHeight;
    if (canvasW <= 0 || canvasH <= 0) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    nodeIds.forEach(id => {
      const node = nodes[id];
      const el = document.getElementById(`node-${id}`);
      const w = el ? el.offsetWidth : 200;
      const h = el ? el.offsetHeight : 100;

      minX = Math.min(minX, node.pos_x);
      minY = Math.min(minY, node.pos_y);
      maxX = Math.max(maxX, node.pos_x + w);
      maxY = Math.max(maxY, node.pos_y + h);
    });

    const bbWidth = maxX - minX;
    const bbHeight = maxY - minY;
    if (bbWidth <= 0 || bbHeight <= 0) return;

    const padding = 80;
    const zoom = Math.min(
      (canvasW - padding) / bbWidth,
      (canvasH - padding) / bbHeight,
      1
    );
    const clampedZoom = Math.max(editor.zoom_min, Math.min(editor.zoom_max, zoom));

    const bbCenterX = (minX + maxX) / 2;
    const bbCenterY = (minY + maxY) / 2;

    editor.zoom = clampedZoom;
    editor.canvas_x = (canvasW / 2) - (bbCenterX * clampedZoom);
    editor.canvas_y = (canvasH / 2) - (bbCenterY * clampedZoom);

    editor.precanvas.style.transform =
      `translate(${editor.canvas_x}px, ${editor.canvas_y}px) scale(${clampedZoom})`;

    document.querySelector(".zoom-level").innerHTML = Math.round(clampedZoom * 100) + '%';
  }

  setupDragEvent() {
    /* DRAG EVENT - Mouse and Touch Actions */
    const elements = document.getElementsByClassName('draggable-item');
    for (let i = 0; i < elements.length; i++) {
      elements[i].addEventListener('touchend', this.drop, false);
      elements[i].addEventListener('touchstart', this.drag, false );
    }
  }

  allowDrop(ev) {
    ev.preventDefault();
  }

  drag(ev) {
    ev.dataTransfer.setData("type", ev.target.getAttribute('data-type'));
  }

  drop(ev) {
    ev.preventDefault();
    const data = ev.dataTransfer.getData("type");
    this.addNodeToDrawFlow(data, ev.clientX, ev.clientY);
  }

  addNodeToDrawFlow(name, pos_x, pos_y) {
    const editor = this.editor;
    if(editor.editor_mode === 'fixed') {
      return false;
    }

    pos_x = pos_x * ( editor.precanvas.clientWidth / (editor.precanvas.clientWidth * editor.zoom)) - (editor.precanvas.getBoundingClientRect().x * ( editor.precanvas.clientWidth / (editor.precanvas.clientWidth * editor.zoom)));
    pos_y = pos_y * ( editor.precanvas.clientHeight / (editor.precanvas.clientHeight * editor.zoom)) - (editor.precanvas.getBoundingClientRect().y * ( editor.precanvas.clientHeight / (editor.precanvas.clientHeight * editor.zoom)));

    switch (name) {
      case 'trigger':
        return editor.addNode('trigger', 0, 1, pos_x, pos_y, 'trigger', {}, this.getNodeContent("trigger-node"));
      case 'email':
        return editor.addNode('email', 1, 1, pos_x, pos_y, 'email', {}, this.getNodeContent("email-node"));
      case 'end':
        return editor.addNode('end', 1, 0, pos_x, pos_y, 'end', {}, this.getNodeContent("end-node"));
      case 'filter-string':
        return editor.addNode('filter-string', 1, 1, pos_x, pos_y, 'filter-string', {}, this.getNodeContent("filter-string-node"));
      case 'filter-integer':
        return editor.addNode('filter-integer', 1, 1, pos_x, pos_y, 'filter-integer', {}, this.getNodeContent("filter-integer-node"));
      case 'filter-date':
        return editor.addNode('filter-date', 1, 1, pos_x, pos_y, 'filter-date', {}, this.getNodeContent("filter-date-node"));
      case 'filter-birthday':
        return editor.addNode('filter-birthday', 1, 1, pos_x, pos_y, 'filter-birthday', {}, this.getNodeContent("filter-birthday-node"));
      case 'filter-gender':
        return editor.addNode('filter-gender', 1, 1, pos_x, pos_y, 'filter-gender', {}, this.getNodeContent("filter-gender-node"));
      case 'filter-location':
        return editor.addNode('filter-location', 1, 1, pos_x, pos_y, 'filter-location', {}, this.getNodeContent("filter-location-node"));
      case 'filter-guest_tag':
        return editor.addNode('filter-guest_tag', 1, 1, pos_x, pos_y, 'filter-guest_tag', {}, this.getNodeContent("filter-guest_tag-node"));
      case 'filter-provider':
        return editor.addNode('filter-provider', 1, 1, pos_x, pos_y, 'filter-provider', {}, this.getNodeContent("filter-provider-node"));
      case 'filter-form':
        return editor.addNode('filter-form', 1, 1, pos_x, pos_y, 'filter-form', {}, this.getNodeContent("filter-form-node"));
      case 'filter-review':
        return editor.addNode('filter-review', 1, 1, pos_x, pos_y, 'filter-review', {}, this.getNodeContent("filter-review-node"));
      case 'filter-duration-range':
        return editor.addNode('filter-duration-range', 1, 1, pos_x, pos_y, 'filter-duration-range', {}, this.getNodeContent("filter-duration-range-node"));
      case 'filter-continuously-sent':
        return editor.addNode('filter-continuously-sent', 1, 1, pos_x, pos_y, 'filter-continuously-sent', {}, this.getNodeContent("filter-continuously-sent-node"));
      case 'filter-is_subscribed':
        return editor.addNode('filter-is_subscribed', 1, 1, pos_x, pos_y, 'filter-is_subscribed', {}, this.getNodeContent("filter-is_subscribed-node"));
      case 'filter-sms_subscribed':
        return editor.addNode('filter-sms_subscribed', 1, 1, pos_x, pos_y, 'filter-sms_subscribed', {}, this.getNodeContent("filter-sms_subscribed-node"));
      default:
    }
  }

  getNodeContent(className) {
    return document.querySelector("." + className).outerHTML;
  }

  refreshConnections() {
    requestAnimationFrame(() => {
      const nodes = this.editor.drawflow.drawflow.Home.data;
      Object.keys(nodes).forEach(id => {
        this.editor.updateConnectionNodes(`node-${id}`);
      });
    });

    setTimeout(() => {
      this.refreshConnections();
    }, 10);
  }

  setupConnectionClickHandler() {
    this.editor.precanvas.addEventListener('click', (e) => {
      if (e.target.classList.contains('main-path')) {
        e.stopPropagation();
        this.showConnectionDeleteBtn(e.target);
      } else if (!e.target.closest('#connection-delete-btn')) {
        this.hideConnectionDeleteBtn();
      }
    });
  }

  showConnectionDeleteBtn(pathEl) {
    this.hideConnectionDeleteBtn();

    const svg = pathEl.parentElement;
    const classes = [...svg.classList];

    const inClass = classes.find(c => c.startsWith('node_in_node-'));
    const outClass = classes.find(c => c.startsWith('node_out_node-'));
    if (!inClass || !outClass) return;

    const inputId = parseInt(inClass.replace('node_in_node-', ''));
    const outputId = parseInt(outClass.replace('node_out_node-', ''));
    const outputPort = classes.find(c => c.startsWith('output_')) || 'output_1';
    const inputPort = classes.find(c => c.startsWith('input_')) || 'input_1';

    const midPoint = pathEl.getPointAtLength(pathEl.getTotalLength() / 2);

    const btn = document.createElement('div');
    btn.id = 'connection-delete-btn';
    btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    Object.assign(btn.style, {
      position: 'absolute',
      left: `${midPoint.x - 11}px`,
      top: `${midPoint.y - 11}px`,
      width: '22px',
      height: '22px',
      background: '#ef4444',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: '9999',
      boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
    });

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.editor.removeSingleConnection(outputId, inputId, outputPort, inputPort);
      this.hideConnectionDeleteBtn();
    });

    this.editor.precanvas.appendChild(btn);
  }

  hideConnectionDeleteBtn() {
    const btn = document.getElementById('connection-delete-btn');
    if (btn) btn.remove();
  }

  showActionMenu(e) {
    e.preventDefault();
    e.stopPropagation();
    const menu = document.querySelector("#actions-menu");
    if (!menu) return;

    this.selectedNode = e.currentTarget.closest("[id^='node-']");

    const rect = e.currentTarget.getBoundingClientRect();
    Object.assign(menu.style, {
      zIndex: "9999",
      display: "block",
      position: "fixed",
      top: `${rect.top + 30}px`,
      left: `${rect.right - 65}px`
    });
  }

  hideActionMenu = (e) => {
    const menu = document.querySelector("#actions-menu");
    if (!menu.contains(e.target) || menu) {
      menu.style.display = "none";
    }
  };

  editNode(e) {
    const node = this.selectedNode;
    if (!Boolean(node || this.editor)) return;

    this.storeNodeSnapshot(node);
    this.showEditActions();
    this.hideActionMenu(e);
  }

  showEditActions () {
    const node = this.selectedNode;
    if (node.querySelector(".node-edit-actions")) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = `<div class="node-edit-actions">
      <button type="button" class="node-edit-btn node-save-btn" data-action="click->mission-control--draw-flow#saveAction">Save</button>
      <button type="button" class="node-edit-btn node-discard-btn" data-action="click->mission-control--draw-flow#discardAction">Discard</button>
    </div>`

    node.classList.add("node-editing");
    node.querySelector(".node-content").appendChild(wrapper);
  }

  storeNodeSnapshot(node) {
    const snapshot = [];
    node.querySelectorAll("select, input").forEach((el) => {
      snapshot.push({ el, value: el.value });
    });
    node.editSnapshot = snapshot;
  }

  finishEditing() {
    const node = this.selectedNode
    delete node.editSnapshot;

    node.classList.remove("node-editing");

    const actions = node.querySelector(".node-edit-actions");
    if (actions) actions.remove();
  }

  toggleFilterValue(e) {
    const select = e.currentTarget;
    const input = select.closest('.delay-input-group').querySelector('.filter-value');
    if (!input) return;

    const hideInput = ["has any value", "is unknown"].includes(select.value);
    input.style.display = hideInput ? "none" : "";
  }

  saveAction(e) {
    e.stopPropagation();
    this.finishEditing();
  }

  discardAction(e) {
    e.stopPropagation();
    this.selectedNode.editSnapshot.forEach(({ el, value }) => { el.value = value; });
    this.finishEditing();
  }

  cloneNode(e) {
    if (!Boolean(this.selectedNode || this.editor)) return;

    let nodeId = this.selectedNode.id.replace("node-", "");
    const node = this.editor.getNodeFromId(nodeId);
    if (node) {
      const pos_x = (node.pos_x || 0) + 50;
      const pos_y = (node.pos_y || 0) + 50;
      this.editor.addNode(node.name, Object.keys(node.inputs).length, Object.keys(node.outputs).length, pos_x, pos_y, node.class, node.data || {}, node.html);
    }
    this.hideActionMenu(e);
  }

  deleteNode(e) {
    if (!Boolean(this.selectedNode || this.editor)) return;

    this.editor.removeNodeId(this.selectedNode.id);
    this.hideActionMenu(e);
  }

  applyFilters(e) {
    const btn = e.currentTarget;
    btn.disabled = true;
    btn.classList.add("loading");

    const request = this.findPathToEnd();
    if (request && request.done) {
      request
        .done(() => {
          this.syncFlowToTrigger();
          this.switchToTriggersView();
        })
        .always(() => {
          btn.disabled = false;
          btn.classList.remove("loading");
        });
    } else {
      btn.disabled = false;
      btn.classList.remove("loading");
    }
  }

  findPathToEnd() {
    const nodes = this.editor.drawflow.drawflow.Home.data;
    let currentNode = Object.values(nodes).find(n => n.name === "trigger");
    if (!currentNode) return;

    const paramsArray = [{"query_type": "is unknown", "filter_type": "archived_at", "search_field": "archived_at", "search_value": "is unknown"}];
    const skipNodes = ["trigger", "email", "end"];
    let reachedEnd = false;

    while (currentNode) {
      if (currentNode.name === "end") { reachedEnd = true; break; }

      if (!skipNodes.includes(currentNode.name)) {
        const domNode = document.getElementById(`node-${currentNode.id}`);
        if (domNode) {
          const filter = this.extractFilterData(domNode);
          if (filter) paramsArray.push(filter);
        }
      }

      const connections = currentNode.outputs?.output_1?.connections;
      currentNode = connections?.length > 0 ? nodes[connections[0].node] : null;
    }

    if (!reachedEnd) {
      toastr.error("All nodes must be connected to the End node.");
      return;
    }

    if (paramsArray.length === 0) return;

    const filterController = this.application.getControllerForElementAndIdentifier(
      this.element, "guest-database--filter-guests"
    );

    if (filterController) {
      filterController.params = paramsArray;
      const { renderUrl, cacheKey } = this.element.dataset;
      return filterController.fetchData(renderUrl, {
        segment: "filter",
        cache_key: cacheKey,
        params_array: paramsArray
      });
    }
  }

  extractFilterData(domNode) {
    const getValue = (selector) => {
      const el = domNode.querySelector(selector);
      return el ? el.value : null;
    };

    const filterType = getValue("input[name*='filter_type']");
    const searchField = getValue("input[name*='search_field']")
      || getValue("select[name*='search_field']");
    const queryType = getValue("select[name*='query_type']");
    let searchValue = getValue("input[name*='search_value']")
      || getValue("select[name*='search_value']");

    if (!searchValue || ["is unknown", "has any value"].includes(queryType)) {
      searchValue = queryType || searchValue;
    }

    const filter = {
      filter_type: filterType,
      search_field: searchField,
      query_type: queryType || searchValue,
      search_value: searchValue
    };

    if (filterType === "duration-range") {
      const startDate = getValue("input.filter-start-date");
      const endDate = getValue("input.filter-end-date");
      if (startDate) filter.start_date = new Date(startDate).toISOString();
      if (endDate) filter.end_date = new Date(endDate).toISOString();
    }

    return filter;
  }

  loadFiltersToFlow(paramsArray) {
    const editor = this.editor;
    const nodes = editor.drawflow.drawflow.Home.data;

    const keepTypes = ["trigger", "email", "end"];
    Object.values(nodes).forEach(n => {
      if (!keepTypes.includes(n.name)) {
        editor.removeNodeId(`node-${n.id}`);
      }
    });

    const remaining = editor.drawflow.drawflow.Home.data;
    const triggerNode = Object.values(remaining).find(n => n.name === "trigger");
    const emailNode = Object.values(remaining).find(n => n.name === "email");
    const endNode = Object.values(remaining).find(n => n.name === "end");
    if (!triggerNode || !emailNode || !endNode) return;

    const flowFilters = paramsArray.filter(f => {
      return document.querySelector(`.filter-${f.filter_type}-node`);
    });

    const posX = emailNode.pos_x;
    const posY = emailNode.pos_y + this.nodeGap;

    endNode.pos_y = posY + (flowFilters.length * this.nodeGap) + this.nodeGap;
    const endDom = document.querySelector(`#node-${endNode.id}`);
    if (endDom) endDom.style.top = `${endNode.pos_y}px`;

    let prevNodeId = emailNode.id;

    flowFilters.forEach((filterData, i) => {
      const nodeName = `filter-${filterData.filter_type}`;
      const templateEl = document.querySelector(`.${nodeName}-node`);

      const newNodeId = editor.addNode(nodeName, 1, 1, posX, posY + i * this.nodeGap, nodeName, {}, templateEl.outerHTML);
      this.setNodeFilterValues(newNodeId, filterData);
      editor.addConnection(prevNodeId, newNodeId, "output_1", "input_1");
      prevNodeId = newNodeId;
    });

    editor.addConnection(prevNodeId, endNode.id, "output_1", "input_1");
    this.refreshConnections();
    this.updateSequenceJson();
  }

  setNodeFilterValues(nodeId, filterData) {
    const domNode = document.getElementById(`node-${nodeId}`);
    if (!domNode) return;

    const setVal = (selector, value) => {
      const el = domNode.querySelector(selector);
      if (el && value != null) el.value = value;
    };

    setVal("select[name*='search_field']", filterData.search_field);
    setVal("input[name*='search_field']", filterData.search_field);
    setVal("select[name*='query_type']", filterData.query_type);
    setVal("select[name*='search_value']", filterData.search_value);

    const hideInput = ["has any value", "is unknown"].includes(filterData.query_type);
    const filterValueInput = domNode.querySelector("input[name*='search_value']");
    if (filterValueInput) {
      filterValueInput.style.display = hideInput ? "none" : "";
      if (!hideInput) filterValueInput.value = filterData.search_value ?? "";
    }

    if (filterData.filter_type === "duration-range") {
      setVal("input[name*='start-date']", filterData.start_date);
      setVal("input[name*='end-date']", filterData.end_date);
    }
  }
}
